import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { X, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";

export default function CariImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/cari/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cari_sablon.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Şablon indirilemedi");
    }
  };

  const runPreview = async () => {
    if (!file) {
      toast.error("Lütfen bir Excel dosyası seçin");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cari/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.total === 0) {
        toast.error("Dosyada veri satırı bulunamadı");
        setFile(null);
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Önizleme başarısız");
    } finally {
      setLoading(false);
    }
  };

  const confirmImport = async () => {
    const validRows = preview.rows.filter((r) => r.valid).map((r) => r.data);
    if (validRows.length === 0) {
      toast.error("İçe aktarılacak geçerli satır yok");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/cari/import/confirm", { records: validRows });
      toast.success(`${data.inserted} cari müşteri içe aktarıldı`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İçe aktarma başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className={`bg-white border border-[#E7DECC] rounded-sm w-full my-10 ${preview ? "max-w-3xl" : "max-w-lg"}`} data-testid="cari-import-dialog">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <h3 className="font-heading text-xl font-bold tracking-tight">
            {preview ? "Cari İçe Aktarma Önizlemesi" : "Cari — Excel ile Toplu Ekle"}
          </h3>
          <button onClick={onClose} aria-label="Kapat" data-testid="cari-import-close" className="text-[#8C8377] hover:text-[#26231E]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!preview ? (
          <div className="p-6">
            <div className="border border-[#E7DECC] bg-[#F3ECDD] rounded-sm p-4 mb-5">
              <div className="kicker mb-2">1. ŞABLONU İNDİRİN</div>
              <p className="text-sm text-[#8C8377] mb-3">Sütunlar: Müşteri, Vade Tarihi, Tutar, Açıklama. Kalan ve durum otomatik hesaplanır.</p>
              <button onClick={downloadTemplate} data-testid="cari-download-template" className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">
                <Download className="w-4 h-4 text-[#B8933F]" /> Şablonu İndir (.xlsx)
              </button>
            </div>
            <div className="kicker mb-2">2. DOSYAYI SEÇİN VE ÖNİZLEYİN</div>
            <label htmlFor="cari-import-file" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[#E7DECC] rounded-sm py-8 cursor-pointer hover:border-[#B8933F] transition-colors duration-150">
              <FileSpreadsheet className="w-8 h-8 text-[#2F6E4E]" />
              <span className="text-sm text-[#8C8377]">{file ? file.name : "Dosya seçmek için tıklayın (.xlsx)"}</span>
              <input id="cari-import-file" type="file" accept=".xlsx" className="hidden" data-testid="cari-import-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">İptal</button>
              <button onClick={runPreview} disabled={loading} data-testid="cari-import-preview-button" className="flex items-center gap-1.5 px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60">
                <Upload className="w-4 h-4" /> {loading ? "İşleniyor..." : "Önizle"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="flex items-center gap-1.5 text-sm"><CheckCircle2 className="w-4 h-4 text-[#2F6E4E]" /><span className="font-semibold">{preview.valid_count}</span> geçerli</div>
              <div className="flex items-center gap-1.5 text-sm"><AlertCircle className="w-4 h-4 text-[#A63A3A]" /><span className="font-semibold">{preview.invalid_count}</span> hatalı</div>
              <span className="text-sm text-[#8C8377]">Toplam {preview.total}</span>
            </div>
            <div className="border border-[#E7DECC] rounded-sm overflow-auto max-h-[55vh]" data-testid="cari-preview-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#F3ECDD]">
                  <tr className="border-b border-[#E7DECC]">
                    <th className="px-2 py-2 kicker text-left w-10">#</th>
                    <th className="px-2 py-2 kicker text-left">Müşteri</th>
                    <th className="px-2 py-2 kicker text-left">Vade</th>
                    <th className="px-2 py-2 kicker text-right">Tutar</th>
                    <th className="px-2 py-2 kicker text-left">Durum</th>
                    <th className="px-2 py-2 kicker text-left">Sonuç</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.row_no} className={`border-b border-[#E7DECC] ${r.valid ? "" : "bg-[#A63A3A]/5"}`}>
                      <td className="px-2 py-1.5 text-[#8C8377]">{r.row_no}</td>
                      <td className="px-2 py-1.5 font-medium">{r.data.musteri || "-"}</td>
                      <td className="px-2 py-1.5 tabular-nums">{fmtDate(r.data.vade_tarihi)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.data.tutar)}</td>
                      <td className="px-2 py-1.5">{r.computed.durum}</td>
                      <td className="px-2 py-1.5">
                        {r.valid ? (
                          <span className="inline-flex items-center gap-1 text-[#2F6E4E] text-xs font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> Geçerli</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#A63A3A] text-xs font-semibold">{r.errors.join(", ")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between gap-3 mt-6">
              <button onClick={() => setPreview(null)} className="flex items-center gap-1.5 px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"><ArrowLeft className="w-4 h-4" /> Geri</button>
              <button onClick={confirmImport} disabled={loading || preview.valid_count === 0} data-testid="cari-import-confirm-button" className="flex items-center gap-1.5 px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-50">
                <Upload className="w-4 h-4" /> {loading ? "Kaydediliyor..." : `${preview.valid_count} Kaydı İçe Aktar`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
